import React, { useEffect, useMemo, useRef } from "react"
import { yupResolver } from "@hookform/resolvers/yup"
import useAppDispatch from "hooks/useAppDispatch"
import useAppSelector from "hooks/useAppSelector"
import { SDCompaniesInterface } from "types/staticData.types"
import { SDCompaniesInitialValues, SDCompaniesSchema } from "Schema/StaticData/SDCompanies"
import { AntdButton, FormInputAntd, GlobalSidebar, SidebarList } from "components"
import { FormProvider, SubmitHandler, useForm } from "react-hook-form"
import {
	getSDCompanies,
	saveSDCompanies,
	selectSDCompaniesById,
	selectSDCompaniesList,
	isSDCompaniesLoading,
	isSDCompaniesSaving,
} from "store/StaticData/SDCompanies.slice"
import { useAdminQueryParams } from "hooks"
import { sortByProperty } from "config/utils"

interface EquipmentTypeFormHandler extends SDCompaniesInterface {}

const SDCompanies = () => {
	const dispatch = useAppDispatch()
	const saving = useAppSelector(isSDCompaniesSaving())
	const loading = useAppSelector(isSDCompaniesLoading())
	const SDCompaniesList = useAppSelector(selectSDCompaniesList)
	const { params, setSearchParams } = useAdminQueryParams()
	const { selected: selectedCompany = "" } = params
	const SDCompanies = useAppSelector(selectSDCompaniesById(selectedCompany || ""))
	const isNewBeingCreated = useRef(false)
	const methods = useForm<EquipmentTypeFormHandler>({ resolver: yupResolver(SDCompaniesSchema) })
	const {
		handleSubmit,
		reset,
		formState: { isSubmitted, isSubmitting, isDirty },
	} = methods
	const sidebarSDCompanies = useMemo(() => {
		return (SDCompaniesList ?? []).map(SDCompanies => ({
			_id: SDCompanies._id,
			name: SDCompanies.name,
		}))
	}, [saving, SDCompaniesList?.length, isSubmitted])

	const onSubmit: SubmitHandler<EquipmentTypeFormHandler> = data => {
		isNewBeingCreated.current = true
		dispatch(
			saveSDCompanies(data, id => {
				setSearchParams({ selected: id })
				reset(data)
			}),
		)
	}

	useEffect(() => {
		dispatch(getSDCompanies(true))
	}, [])

	useEffect(() => {
		if (!selectedCompany && !!SDCompaniesList?.length && !isNewBeingCreated.current) {
			setSearchParams({ selected: SDCompaniesList[0]._id })
		}
	}, [params, SDCompaniesList])

	useEffect(() => {
		if (selectedCompany) {
			if (SDCompanies) {
				reset({ ...SDCompaniesInitialValues, ...SDCompanies })
			}
		} else if (!selectedCompany && !isNewBeingCreated.current) {
			reset({ ...SDCompaniesInitialValues })
		}
	}, [selectedCompany, SDCompanies])

	const handleSelected = (SDCompaniesId: string) => {
		setSearchParams({ selected: SDCompaniesId })
	}

	return (
		<>
			<GlobalSidebar>
				<SidebarList
					title={"Companies"}
					loading={loading}
					selected={selectedCompany}
					setSelected={handleSelected}
					list={sidebarSDCompanies.sort(sortByProperty("name"))}
				/>
			</GlobalSidebar>
			<FormProvider {...methods}>
				<form onSubmit={handleSubmit(onSubmit)}>
					<div className="flex w-1/4 flex-col gap-y-2">
						<FormInputAntd type="text" label="Company Name" name="name" />
						<div className="flex gap-x-2">
							<AntdButton
								onClick={() => {
									isNewBeingCreated.current = true
									reset({ ...SDCompaniesInitialValues })
									setSearchParams({ selected: "" })
								}}
							>
								New
							</AntdButton>
							<AntdButton disabled={isSubmitting || !isDirty} loading={saving} htmlType="submit">
								{selectedCompany ? "Update" : "Save"}
							</AntdButton>
						</div>
					</div>
				</form>
			</FormProvider>
		</>
	)
}

export default SDCompanies
